/**
 * 🔐 SEC-003 FIX: Stockage 100% sécurisé
 * 
 * Ce fichier utilise UNIQUEMENT secureStorage pour les données sensibles.
 * Les fonctions synchrones sont remplacées par des versions async.
 * 
 * IMPORTANT: Les données ne sont accessibles QUE si l'utilisateur est connecté.
 * Pour les utilisateurs non connectés, les données sont en mémoire uniquement.
 */

import { Pet, Vaccine, WeightEntry, PetPhoto, MedicationRecord, ActivityRecord, Appointment, 
  VeterinarianContact, PetLocation, DewormingRecord, FoodRecord, PracticalInfo, 
  DentalCareRecord, HoofCareRecord, InjuryRecord, SanitaryVisitRecord } from '../types/PetTypes';
import { secureStorage, isSecureStorageAvailable } from '@/utils/secureStorage';
import { logger } from '@/utils/logger';

// ============================================
// GESTION DE L'UTILISATEUR COURANT
// ============================================

let currentUserId: string | null = null;

// Cache mémoire pour les utilisateurs non connectés et le mode hors-ligne
const memoryCache: Record<string, unknown> = {};

export const setCurrentUserId = (userId: string | null): void => {
  currentUserId = userId;
  if (!userId) {
    // Vider le cache mémoire à la déconnexion
    Object.keys(memoryCache).forEach(key => delete memoryCache[key]);
    secureStorage.clearKeyCache();
  }
  logger.debug('localStorage: userId set', userId ? 'present' : 'null');
};

export const getCurrentUserId = (): string | null => currentUserId;

// ============================================
// HELPER GÉNÉRIQUE POUR CHARGEMENT/SAUVEGARDE SÉCURISÉ
// ============================================

async function loadSecure<T>(key: string, defaultValue: T[]): Promise<T[]> {
  if (!currentUserId) {
    // Utilisateur non connecté: utiliser cache mémoire
    return (memoryCache[key] as T[]) || defaultValue;
  }
  
  if (!isSecureStorageAvailable()) {
    logger.warn(`Secure storage not available for ${key}`);
    return (memoryCache[key] as T[]) || defaultValue;
  }
  
  try {
    const data = await secureStorage.getItem<T[]>(key, currentUserId);
    if (data) {
      memoryCache[key] = data; // Mettre en cache pour accès sync
    }
    return data || defaultValue;
  } catch (error) {
    logger.error(`Error loading ${key}:`, error);
    return defaultValue;
  }
}

async function saveSecure<T>(key: string, data: T[]): Promise<void> {
  // Toujours mettre en cache mémoire pour accès immédiat
  memoryCache[key] = data;
  
  if (!currentUserId) {
    logger.debug(`${key} saved to memory only (user not logged in)`);
    return;
  }
  
  if (!isSecureStorageAvailable()) {
    logger.warn(`Secure storage not available for ${key}`);
    return;
  }
  
  try {
    await secureStorage.setItem(key, data, currentUserId);
  } catch (error) {
    logger.error(`Error saving ${key}:`, error);
  }
}

// ============================================
// FONCTIONS SYNCHRONES (Accès cache mémoire)
// Utiliser les versions ASYNC pour les vraies opérations
// ============================================

// Pets
export const loadPets = (): Pet[] => {
  return (memoryCache['pets'] as Pet[]) || [];
};

export const savePets = (pets: Pet[]): void => {
  memoryCache['pets'] = pets;
  if (currentUserId && isSecureStorageAvailable()) {
    secureStorage.setItem('pets', pets, currentUserId).catch(err => 
      logger.error('Error saving pets:', err)
    );
  }
};

// Async versions (recommandées)
export const loadPetsAsync = (): Promise<Pet[]> => loadSecure<Pet>('pets', []);
export const savePetsAsync = (pets: Pet[]): Promise<void> => saveSecure('pets', pets);

// Vaccines
export const loadVaccines = (): Vaccine[] => {
  return (memoryCache['vaccines'] as Vaccine[]) || [];
};

export const saveVaccines = (vaccines: Vaccine[]): void => {
  memoryCache['vaccines'] = vaccines;
  if (currentUserId && isSecureStorageAvailable()) {
    secureStorage.setItem('vaccines', vaccines, currentUserId).catch(err => 
      logger.error('Error saving vaccines:', err)
    );
  }
};

export const loadVaccinesAsync = (): Promise<Vaccine[]> => loadSecure<Vaccine>('vaccines', []);
export const saveVaccinesAsync = (vaccines: Vaccine[]): Promise<void> => saveSecure('vaccines', vaccines);

// Weight Entries
export const loadWeightEntries = (): WeightEntry[] => {
  return (memoryCache['weightEntries'] as WeightEntry[]) || [];
};

export const saveWeightEntries = (weightEntries: WeightEntry[]): void => {
  memoryCache['weightEntries'] = weightEntries;
  if (currentUserId && isSecureStorageAvailable()) {
    secureStorage.setItem('weightEntries', weightEntries, currentUserId).catch(err => 
      logger.error('Error saving weightEntries:', err)
    );
  }
};

export const loadWeights = loadWeightEntries;
export const saveWeights = saveWeightEntries;

// Photos
export const loadPhotos = (): PetPhoto[] => {
  return (memoryCache['photos'] as PetPhoto[]) || [];
};

export const savePhotos = (photos: PetPhoto[]): void => {
  memoryCache['photos'] = photos;
  if (currentUserId && isSecureStorageAvailable()) {
    secureStorage.setItem('photos', photos, currentUserId).catch(err => 
      logger.error('Error saving photos:', err)
    );
  }
};

// Medications
export const loadMedications = (): MedicationRecord[] => {
  return (memoryCache['medications'] as MedicationRecord[]) || [];
};

export const saveMedications = (medications: MedicationRecord[]): void => {
  memoryCache['medications'] = medications;
  if (currentUserId && isSecureStorageAvailable()) {
    secureStorage.setItem('medications', medications, currentUserId).catch(err => 
      logger.error('Error saving medications:', err)
    );
  }
};

// Activities
export const loadActivities = (): ActivityRecord[] => {
  return (memoryCache['activities'] as ActivityRecord[]) || [];
};

export const saveActivities = (activities: ActivityRecord[]): void => {
  memoryCache['activities'] = activities;
  if (currentUserId && isSecureStorageAvailable()) {
    secureStorage.setItem('activities', activities, currentUserId).catch(err => 
      logger.error('Error saving activities:', err)
    );
  }
};

// Appointments
export const loadAppointments = (): Appointment[] => {
  return (memoryCache['appointments'] as Appointment[]) || [];
};

export const saveAppointments = (appointments: Appointment[]): void => {
  memoryCache['appointments'] = appointments;
  if (currentUserId && isSecureStorageAvailable()) {
    secureStorage.setItem('appointments', appointments, currentUserId).catch(err => 
      logger.error('Error saving appointments:', err)
    );
  }
};

// Veterinarians
export const loadVeterinarians = (): VeterinarianContact[] => {
  return (memoryCache['veterinarians'] as VeterinarianContact[]) || [];
};

export const saveVeterinarians = (veterinarians: VeterinarianContact[]): void => {
  memoryCache['veterinarians'] = veterinarians;
  if (currentUserId && isSecureStorageAvailable()) {
    secureStorage.setItem('veterinarians', veterinarians, currentUserId).catch(err => 
      logger.error('Error saving veterinarians:', err)
    );
  }
};

// Locations
export const loadLocations = (): PetLocation[] => {
  return (memoryCache['locations'] as PetLocation[]) || [];
};

export const saveLocations = (locations: PetLocation[]): void => {
  memoryCache['locations'] = locations;
  if (currentUserId && isSecureStorageAvailable()) {
    secureStorage.setItem('locations', locations, currentUserId).catch(err => 
      logger.error('Error saving locations:', err)
    );
  }
};

// Dewormings
export const loadDewormings = (): DewormingRecord[] => {
  return (memoryCache['dewormings'] as DewormingRecord[]) || [];
};

export const saveDewormings = (dewormings: DewormingRecord[]): void => {
  memoryCache['dewormings'] = dewormings;
  if (currentUserId && isSecureStorageAvailable()) {
    secureStorage.setItem('dewormings', dewormings, currentUserId).catch(err => 
      logger.error('Error saving dewormings:', err)
    );
  }
};

// Food Records
export const loadFoodRecords = (): FoodRecord[] => {
  return (memoryCache['foodRecords'] as FoodRecord[]) || [];
};

export const saveFoodRecords = (foodRecords: FoodRecord[]): void => {
  memoryCache['foodRecords'] = foodRecords;
  if (currentUserId && isSecureStorageAvailable()) {
    secureStorage.setItem('foodRecords', foodRecords, currentUserId).catch(err => 
      logger.error('Error saving foodRecords:', err)
    );
  }
};

// Practical Infos
export const loadPracticalInfos = (): PracticalInfo[] => {
  return (memoryCache['practicalInfos'] as PracticalInfo[]) || [];
};

export const savePracticalInfos = (practicalInfos: PracticalInfo[]): void => {
  memoryCache['practicalInfos'] = practicalInfos;
  if (currentUserId && isSecureStorageAvailable()) {
    secureStorage.setItem('practicalInfos', practicalInfos, currentUserId).catch(err => 
      logger.error('Error saving practicalInfos:', err)
    );
  }
};

// Dental Care Records (Équine)
export const loadDentalCareRecords = (): DentalCareRecord[] => {
  return (memoryCache['dentalCareRecords'] as DentalCareRecord[]) || [];
};

export const saveDentalCareRecords = (records: DentalCareRecord[]): void => {
  memoryCache['dentalCareRecords'] = records;
  if (currentUserId && isSecureStorageAvailable()) {
    secureStorage.setItem('dentalCareRecords', records, currentUserId).catch(err => 
      logger.error('Error saving dentalCareRecords:', err)
    );
  }
};

// Hoof Care Records (Équine)
export const loadHoofCareRecords = (): HoofCareRecord[] => {
  return (memoryCache['hoofCareRecords'] as HoofCareRecord[]) || [];
};

export const saveHoofCareRecords = (records: HoofCareRecord[]): void => {
  memoryCache['hoofCareRecords'] = records;
  if (currentUserId && isSecureStorageAvailable()) {
    secureStorage.setItem('hoofCareRecords', records, currentUserId).catch(err => 
      logger.error('Error saving hoofCareRecords:', err)
    );
  }
};

// Injury Records
export const loadInjuryRecords = (): InjuryRecord[] => {
  return (memoryCache['injuryRecords'] as InjuryRecord[]) || [];
};

export const saveInjuryRecords = (records: InjuryRecord[]): void => {
  memoryCache['injuryRecords'] = records;
  if (currentUserId && isSecureStorageAvailable()) {
    secureStorage.setItem('injuryRecords', records, currentUserId).catch(err => 
      logger.error('Error saving injuryRecords:', err)
    );
  }
};

// Sanitary Visits (Équine)
export const loadSanitaryVisits = (): SanitaryVisitRecord[] => {
  return (memoryCache['sanitaryVisits'] as SanitaryVisitRecord[]) || [];
};

export const saveSanitaryVisits = (sanitaryVisits: SanitaryVisitRecord[]): void => {
  memoryCache['sanitaryVisits'] = sanitaryVisits;
  if (currentUserId && isSecureStorageAvailable()) {
    secureStorage.setItem('sanitaryVisits', sanitaryVisits, currentUserId).catch(err => 
      logger.error('Error saving sanitaryVisits:', err)
    );
  }
};

// ============================================
// CLEAR ALL LOCAL DATA (logout)
// ============================================

export const clearAllLocalData = async (): Promise<void> => {
  try {
    // Clear secure storage
    if (currentUserId) {
      await secureStorage.clear(currentUserId);
    }
    
    // Clear memory cache
    Object.keys(memoryCache).forEach(key => delete memoryCache[key]);
    
    currentUserId = null;
    secureStorage.clearKeyCache();
    logger.debug('All local data cleared');
  } catch (error) {
    logger.error('Failed to clear local data:', error);
  }
};

// ============================================
// MIGRATION HELPER (appelé au login)
// Migre les anciennes données localStorage vers secureStorage
// ============================================

export const migrateLocalStorageToSecure = async (userId: string): Promise<void> => {
  setCurrentUserId(userId);
  
  if (!isSecureStorageAvailable()) {
    logger.warn('Secure storage not available, skipping migration');
    return;
  }
  
  logger.debug('Starting localStorage migration to secure storage');
  
  const keysToMigrate = [
    'pets', 'vaccines', 'weightEntries', 'photos', 'medications',
    'activities', 'appointments', 'veterinarians', 'locations',
    'dewormings', 'foodRecords', 'practicalInfos', 'dentalCareRecords',
    'hoofCareRecords', 'injuryRecords', 'sanitaryVisits', 'gps-trackers'
  ];
  
  for (const key of keysToMigrate) {
    await secureStorage.migrateFromPlainStorage(key, userId);
  }
  
  logger.debug('Migration to secure storage completed');
};

// ============================================
// INITIALISATION DU CACHE MÉMOIRE AU LOGIN
// Charge les données sécurisées en mémoire
// ============================================

export const initializeFromSecureStorage = async (userId: string): Promise<void> => {
  setCurrentUserId(userId);
  
  if (!isSecureStorageAvailable()) {
    logger.warn('Secure storage not available');
    return;
  }
  
  logger.debug('Initializing memory cache from secure storage');
  
  try {
    // Charger toutes les données sécurisées en mémoire
    const keys = [
      'pets', 'vaccines', 'weightEntries', 'photos', 'medications',
      'activities', 'appointments', 'veterinarians', 'locations',
      'dewormings', 'foodRecords', 'practicalInfos', 'dentalCareRecords',
      'hoofCareRecords', 'injuryRecords', 'sanitaryVisits'
    ];
    
    for (const key of keys) {
      const data = await secureStorage.getItem(key, userId);
      if (data) {
        memoryCache[key] = data;
      }
    }
    
    logger.debug('Memory cache initialized from secure storage');
  } catch (error) {
    logger.error('Failed to initialize from secure storage:', error);
  }
};
